"use client";

import { useCallback, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import useSWRInfinite from "swr/infinite";

export interface PaginationResponse<T> {
  items: T[];
  totalCount: number;
  totalPages: number;
  averageRating?: number;
  totalComments?: number;
}

type FetcherFunction<T> = (
  index: number,
  perPage: number
) => Promise<PaginationResponse<T>>;

type WithPaginationProps<T> = {
  keyPrefix: string;
  fetcher: FetcherFunction<T>;
  itemComponent: React.FC<{ item: T; index: number }>;
  perPage?: number;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  wrapUpComponent?: React.ReactNode;
  notFoundComponent?: React.ReactNode;
  loadingMoreText?: React.ReactNode;
  wrapperClassName?: string;
  onFirstPageLoaded?: (meta: {
    averageRating: number;
    totalComments: number;
  }) => void;
  initialItems?: T[];
  initialMeta?: { averageRating: number; totalComments: number };
  allowPage1Revalidate?: boolean;
};

function WithPagination<T>({
  keyPrefix,
  fetcher,
  itemComponent: ItemComponent,
  perPage = 8,
  containerProps = {},
  wrapUpComponent,
  notFoundComponent,
  loadingMoreText,
  wrapperClassName,
  onFirstPageLoaded,
  initialItems = [],
  initialMeta,
  allowPage1Revalidate = false,
}: WithPaginationProps<T>) {
  const getKey = (
    pageIndex: number,
    previousPageData: PaginationResponse<T> | null
  ) => {
    if (previousPageData && previousPageData.items.length === 0) return null;
    if (pageIndex === 0 && initialItems.length > 0) {
      return [`${keyPrefix}-0`, 0, perPage, true];
    }
    return [`${keyPrefix}-${pageIndex}`, pageIndex, perPage];
  };

  const { data, size, setSize, isLoading, isValidating, error } =
    useSWRInfinite(
      getKey,
      ([, pageIndex, perPage, isInitial]: [
        string,
        number,
        number,
        boolean,
      ]) => {
        if (isInitial) {
          return Promise.resolve({
            items: initialItems,
            totalCount: initialMeta?.totalComments ?? 0,
            totalPages: Math.ceil((initialMeta?.totalComments ?? 0) / perPage),
            averageRating: initialMeta?.averageRating ?? 0,
            totalComments: initialMeta?.totalComments ?? 0,
          });
        }
        return fetcher(pageIndex, perPage);
      },
      { revalidateFirstPage: allowPage1Revalidate }
    );

  const allItems = data?.flatMap((page) => page.items) ?? [];
  const totalPages = data?.[0]?.totalPages ?? 1;

  const { ref, inView } = useInView({ threshold: 0 });

  const loadMore = useCallback(() => {
    if (size < totalPages) setSize(size + 1);
  }, [size, totalPages, setSize]);

  useEffect(() => {
    if (inView && !isLoading && !isValidating && size < totalPages) {
      loadMore();
    }
  }, [inView, isLoading, isValidating, size, totalPages, loadMore]);

  useEffect(() => {
    if (onFirstPageLoaded) {
      if (initialMeta) onFirstPageLoaded(initialMeta);
      else if (data?.[0]) {
        const { averageRating = 0, totalComments = 0 } = data[0];
        onFirstPageLoaded({ averageRating, totalComments });
      }
    }
  }, [data, initialMeta, onFirstPageLoaded]);

  if (error) return <div className="text-red-500">Error fetching data</div>;
  if (!data && (!initialItems || initialItems.length === 0))
    return (
      <div className="text-gray-500">{loadingMoreText ?? "Loading..."}</div>
    );
  if (allItems.length === 0)
    return <>{notFoundComponent ?? <div>No items found.</div>}</>;

  return (
    <>
      <div className={wrapperClassName} {...containerProps}>
        {allItems.map((item, index) => (
          <ItemComponent key={index} item={item} index={index} />
        ))}
      </div>

      <div ref={ref} className="text-center mt-4 mb-10">
        {size < totalPages && (
          <div className="text-gray-500">
            {loadingMoreText ?? "Loading more..."}
          </div>
        )}
        {size === totalPages && wrapUpComponent && (
          <div className="mt-4">{wrapUpComponent}</div>
        )}
      </div>
    </>
  );
}

export default WithPagination;
